import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Flex, Icon, ShimmeringText, Text } from '@kognitos/lattice';

const ELAPSED_UPDATE_INTERVAL_MS = 500;

/**
 * Formats an elapsed duration for compact display in chat progress widgets.
 *
 * @param elapsedMs - Duration in milliseconds
 * @returns Human-readable duration
 */
function formatElapsedDuration(elapsedMs: number): string {
  const seconds = Math.max(0, Math.round(elapsedMs / 1000));
  if (seconds < 60) {
    return `${seconds}s`;
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
}

/**
 * Parses a timestamp into epoch milliseconds.
 *
 * @param timestamp - ISO timestamp or Date object
 * @returns Epoch milliseconds, or current time when parsing fails
 */
function getTimestampMs(timestamp?: Date | string): number {
  if (timestamp instanceof Date) {
    return timestamp.getTime();
  }

  if (typeof timestamp === 'string') {
    const parsed = Date.parse(timestamp);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }

  return Date.now();
}

export interface IQuillToolCallWidgetProps {
  /** User-facing description supplied by the agent tool input. */
  description: string;
  /** Tool name from the backend payload, used as a fallback and debug hint. */
  toolName?: string;
  /** Timestamp when the tool-call block was received. */
  startedAt?: Date | string;
  /** Whether the surrounding agent turn is still active. */
  isActive?: boolean;
}

/**
 * Renders a compact, interleaved tool-call progress item in Quill chat.
 *
 * @param props - Widget props
 * @returns Tool-call progress widget
 */
export function QuillToolCallWidget({
  description,
  toolName,
  startedAt,
  isActive = true,
}: IQuillToolCallWidgetProps) {
  const { t } = useTranslation('automation');
  const startedAtMs = useMemo(() => getTimestampMs(startedAt), [startedAt]);
  const [nowMs, setNowMs] = useState(() => Date.now());

  // eslint-disable-next-line no-restricted-syntax -- reason: Timer updates elapsed tool-call duration while the turn is active
  useEffect(() => {
    if (!isActive) {
      setNowMs(Date.now());
      return;
    }

    const intervalId = window.setInterval(() => {
      setNowMs(Date.now());
    }, ELAPSED_UPDATE_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [isActive]);

  const elapsed = formatElapsedDuration(nowMs - startedAtMs);
  const fallbackDescription = toolName
    ? t('automation.quillChat.tool.fallbackDescriptionWithTool', { toolName })
    : t('automation.quillChat.tool.fallbackDescription');
  const displayDescription = description.trim() || fallbackDescription;
  const statusText = t('automation.quillChat.tool.running', { elapsed });

  return (
    <Flex
      align="center"
      gap={2}
      className="min-w-0 rounded-sm border border-border bg-muted/30 px-3 py-2"
      data-testid="quill-tool-call-widget"
    >
      <Icon type="Wrench" className="size-3 text-muted-foreground" />
      <Flex direction="column" gap={1} className="min-w-0 flex-1">
        {isActive ? (
          <div className="block min-w-0 max-w-full overflow-hidden whitespace-nowrap text-xs">
            <ShimmeringText text={displayDescription} duration={1.2} />
          </div>
        ) : (
          <Text
            level="xSmall"
            color="muted"
            weight="normal"
            className="block w-full truncate"
          >
            {displayDescription}
          </Text>
        )}
        {isActive && (
          <Text level="xSmall" color="muted" className="truncate">
            {statusText}
          </Text>
        )}
      </Flex>
    </Flex>
  );
}
